/** @jsx isolateComponent */

import { css } from '@emotion/core';
import type { ReactNode } from 'react';

import { TooltipWrapper } from '../../display/TooltipWrapper';
import { Spinner } from '../../display/Spinner';
import { kebabCaseAriaAttrs } from '../../../utils/formatAriaAttrKeys';
import { Animation, Colors, Typography } from '../../../theme';
import {
  isolateComponent,
  labelStyles,
  withModifier,
} from '@zapier/style-encapsulation';

// TODO: Remove "small" as an option once v5 is the default
type Size = 'small' | 'medium';

type Props = {
  /**
   * Optional custom `aria-label` for the component. By default
   * `aria-label` will be computed based on the `checked` and
   * `indeterminate` props on this component and their corresponding
   * `*Text` props. However because that causes the `aria-label` to
   * change as `ToggleSwitch` is un/checked, that can be confusing
   * for screenreader users in some scenarios. In that case, use this
   * `ariaLabel` prop to clear up any ambiguity.
   */
  ariaLabel?: string;
  /**
   * Indicates whether the switch is checked on or off.
   */
  checked?: boolean;
  /**
   * Text to render when the switch is checked on.
   */
  checkedText: string;
  /**
   * Indicates whether the switch is disabled.
   */
  disabled?: boolean;
  /**
   * HTML id of the input
   */
  id?: string;
  /**
   * Indicates whether the switch is neither checked on nor off.
   */
  indeterminate?: boolean;
  /**
   * Text to render when the switch is neither checked on nor off.
   */
  indeterminateText: string | ReactNode;
  /**
   * Whether the component should display a loading state.
   */
  isLoading?: boolean;
  /**
   * HTML name attribute, typically used for the key that will
   * be sent to the API.
   */
  name?: string;
  /**
   * Whether to show the "Disabled" tooltip when toggle is disabled.
   */
  showDisabledTooltip?: boolean;
  /**
   * The size of the `ToggleSwitch`. @deprecated medium is the only size for V5.
   */
  size: Size;
  /**
   * Handler invoked when the switch changes from checked on or off.
   */
  onChange: React.ChangeEventHandler;
  /**
   * Text to render when the switch is unchecked.
   */
  uncheckedText: string;
  /**
   * HTML value attribute, i.e. the actual value of the input.
   */
  value?: string;
};

const Styles = labelStyles('ToggleSwitch', {
  root: (props: Props) => [
    css`
      ${Typography.paragraph3Bold};
      align-items: stretch;
      color: ${Colors.neutral800};
      cursor: pointer;
      display: inline-flex;
      overflow: hidden;
      border: ${`1px solid ${Colors.neutral600}`};
      transition: all ${Animation.transitionDuration} ease-in-out;

      &:focus-within:not(:hover) {
        box-shadow: 0 0 0 2px ${Colors.blue};
        border-color: transparent;
      }
    `,
    props.size === 'small' &&
      withModifier(
        props.size,
        css`
          width: 40px;
          height: 22px;
          border-radius: 20px;
          font-size: 0px;
          line-height: 0px;
          padding: 3px;
        `
      ),
    props.size === 'medium' &&
      withModifier(
        props.size,
        css`
          min-width: 60px;
          height: 44px;
          border-radius: 30px;
          padding: 6px;
        `
      ),
    !props.checked &&
      withModifier(
        'unchecked',
        css`
          background-color: ${Colors.neutral200};
        `
      ),
    props.checked &&
      withModifier(
        'checked',
        css`
          background-color: ${Colors.success100};
          border: ${`1px solid ${Colors.success400}`};
        `
      ),
    props.indeterminate &&
      withModifier(
        'indeterminate',
        css`
          background-color: ${Colors.warning200};
        `
      ),
    (props.disabled || props.isLoading) &&
      withModifier(
        'disabled',
        css`
          background-color: ${Colors.neutral200};
          color: ${Colors.neutral500};
          cursor: not-allowed;
          border: ${`1px solid ${Colors.neutral600}`};
        `
      ),
  ],

  innerWrap: css`
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
  `,

  // Still needs to be "visible" so it can be tabbed to,
  // but it shouldn't render anything. Take it out of layout
  // flow and hide it with `opacity` for good measure. Also
  // stretch it across the container so tools like VoiceOver
  // show an outline in the correct location.
  input: css`
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    opacity: 0;
    z-index: -1;
  `,

  spinner: [
    css`
      position: absolute;
      top: 0;
      bottom: 0;
      right: 0;
      left: 0;
      display: flex;
      justify-content: center;
      align-items: center;
    `,
  ],

  // This node's text should be centered within the available space
  // outside of the dot. For instance when the dot is on the left,
  // the text will be to the right of the dot, but centered in that
  // available space. When the dot is on the right, the text will be
  // to the left of the dot, but centered in that available space.
  text: (props: Props) => [
    css`
      display: grid;
      // Define to allow 'textInner' nodes to easily stack
      // on top of each other while preserving their widths,
      // setting up the layout for a nice transition.
      grid-template-areas: 'text';
      width: 100%;
      user-select: none;
      text-align: center;
      transition: padding ${Animation.transitionDuration} ease-in-out;
    `,
    !props.checked &&
      withModifier(`unchecked-${props.size}`, {
        paddingLeft: 'calc(30px + 8px)',
        paddingRight: 0,
      }),
    props.checked &&
      withModifier(`checked-${props.size}`, {
        paddingLeft: 0,
        paddingRight: 'calc(30px + 8px)',
      }),
    props.indeterminate &&
      withModifier(
        'indeterminate',
        css`
          padding-left: 0;
          padding-right: 0;
        `
      ),
    props.isLoading &&
      withModifier(
        'loading',
        css`
          visibility: hidden;
          height: 0;
        `
      ),
  ],

  // Render all text, stacked on top of each other, and then visibly hide
  // the `isHidden` text. This ensures that `ToggleSwitch` is as wide as
  // its longest text label, even if that label is hidden.
  textInner: ({ isHidden }: { isHidden: boolean }) => [
    css`
      // See note at 'grid-template-areas' definition.
      grid-area: text;
      transition: opacity 0.1s ease-in-out;
    `,
    isHidden &&
      withModifier(
        'hidden',
        css`
          opacity: 0;
        `
      ),
  ],

  dot: (props: Props) => [
    css`
      display: block;
      border-radius: 100%;
      background-color: ${Colors.neutral600};
      position: absolute;
      top: 0;
      transition: all ${Animation.transitionDuration} ease-in-out;
      width: 30px;
      height: 30px;
    `,
    !props.checked &&
      withModifier(
        'unchecked',
        css`
          left: 0;
          transform: translateX(0);
        `
      ),
    props.checked &&
      // Using `left` and `transform` for proper `transition`.
      // Using `right: auto` for instance won't transition properly.
      // `translate` is used over `calc` since `translate`'s argument
      // is the size of the `dot` itself rather than its parent.
      withModifier(
        'checked',
        css`
          background-color: ${Colors.success400};
          left: 100%;
          transform: translateX(-100%);
        `
      ),
    props.disabled &&
      withModifier(
        'disabled',
        css`
          background-color: ${Colors.neutral400};
        `
      ),
    props.isLoading &&
      withModifier(
        'loading',
        css`
          visibility: hidden;
          height: 0;
        `
      ),
  ],
});

const InnerText = ({
  children,
  isHidden,
}: {
  children: ReactNode;
  isHidden: boolean;
}) => {
  return (
    <span aria-hidden="true" css={Styles.textInner({ isHidden })}>
      {children}
    </span>
  );
};

const getAriaLabel = (props: Props) => {
  if (props.ariaLabel) {
    return props.ariaLabel;
  }
  if (props.isLoading) {
    return 'Loading';
  }
  if (props.indeterminate) {
    return typeof props.indeterminateText === 'string'
      ? props.indeterminateText
      : 'Indeterminate';
  }
  return props.checked ? props.checkedText : props.uncheckedText;
};

const getTooltipContent = (props: Props) =>
  props.disabled && props.showDisabledTooltip ? 'Disabled' : '';

/**
 * `ToggleSwitch` is a stylized checkbox used to indicate whether something is on or off.
 */
export const ToggleSwitch = (props: Props) => {
  return (
    <TooltipWrapper content={getTooltipContent(props)}>
      {({ childProps }) => (
        <label {...kebabCaseAriaAttrs(childProps)} css={Styles.root(props)}>
          <span css={Styles.innerWrap}>
            <input
              // Not all screenreaders announce the `disabled` attribute,
              // so add `aria-disabled` to reinforce.
              aria-disabled={props.disabled || props.isLoading}
              aria-label={getAriaLabel(props)}
              checked={props.checked}
              css={Styles.input}
              disabled={props.disabled || props.isLoading}
              id={props.id}
              name={props.name}
              onChange={props.onChange}
              type="checkbox"
              value={props.value}
            />
            {props.isLoading && (
              <span css={Styles.spinner}>
                <Spinner />
              </span>
            )}
            <span css={Styles.text(props)}>
              {/* See note at `Styles.textInner` for why all texts are rendered. */}
              <InnerText isHidden={props.indeterminate || !!props.checked}>
                {props.uncheckedText}
              </InnerText>
              <InnerText isHidden={props.indeterminate || !props.checked}>
                {props.checkedText}
              </InnerText>
              <InnerText isHidden={!props.indeterminate}>
                {props.indeterminateText}
              </InnerText>
            </span>
            {!props.indeterminate && !props.isLoading && (
              <span
                aria-hidden="true"
                css={Styles.dot(props)}
                role="presentation"
              />
            )}
          </span>
        </label>
      )}
    </TooltipWrapper>
  );
};

ToggleSwitch.defaultProps = {
  checkedText: 'On',
  indeterminateText: 'N/A',
  isLoading: false,
  onChange: () => {},
  showDisabledTooltip: true,
  size: 'medium',
  uncheckedText: 'Off',
};
